/**
 * 组图台本的三条纯函数(B1-3,2026-07-31)——「谁是封面」与「顺序」。
 *
 * ═══ 为什么封面身份只由位置决定 ═══════════════════════════════════════════
 * 封面就是轮播的第一张。如果允许"某条被标成封面"独立于位置存在,排序之后就会出现
 * 「第 2 条上写着封面」这种说不清的状态,而**页面上没有任何地方能表达它**。
 *
 * 所以持久化的 role 只有一个显式值:`"page"` 打在第 1 条上,含义是
 * **用户把封面这一档关掉了**(整组都当内页出)。其余一律不写。
 *
 * ai 侧同时支持显式 `"cover"`(`GroupPoint.role`),那是给分步续出用的:
 * 续出批发的是第 2..N 条,下标 0 是内页 —— 那条链路必须显式标,不能靠位置。
 * 两边的默认口径一致:不给 role 时,下标 0 = 封面。
 */
import type { GroupPointForm } from "../blueprint";

/** 第 i 条是不是封面 —— 位置说了算,只有第 1 条显式 `page` 时整组无封面。 */
export function isCoverAt(points: GroupPointForm[], i: number): boolean {
  if (i !== 0) return false;
  return points[0]?.role !== "page";
}

/** 这一组有没有封面(= 第 1 条没被关掉)。 */
export function hasCover(points: GroupPointForm[]): boolean {
  return points.length > 0 && isCoverAt(points, 0);
}

/**
 * 归一 role —— **只有第 1 条的 role 有意义**,其余全清。
 *
 * ★每次改动顺序/增删之后都要过一遍:否则「原来的第 1 条被移到第 3 位,身上还带着
 *   role」就会让 ai 侧按显式值判成封面,出一张位置在中间的钩子图,而且不报错。
 */
export function normalizeRoles(points: GroupPointForm[]): GroupPointForm[] {
  return points.map((p, i) => {
    if (i === 0) return p;
    if (p.role === undefined) return p;
    const { role: _drop, ...rest } = p;
    return rest;
  });
}

/** 关/开封面档 —— 写在第 1 条上。 */
export function setCoverEnabled(points: GroupPointForm[], enabled: boolean): GroupPointForm[] {
  if (points.length === 0) return points;
  const [first, ...rest] = points;
  const next = enabled ? { ...first, role: undefined } : { ...first, role: "page" as const };
  if (next.role === undefined) delete next.role;
  return [next, ...rest];
}

/** 上移/下移一条(越界原样返回);移完必过 `normalizeRoles`。 */
export function movePoint(points: GroupPointForm[], from: number, to: number): GroupPointForm[] {
  if (from === to || from < 0 || to < 0 || from >= points.length || to >= points.length) {
    return points;
  }
  const next = [...points];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return normalizeRoles(next);
}
